import React, { useMemo, useCallback, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'
import type { Task } from '../../hooks/useTasks'

interface GanttChartProps {
  tasks: Task[]
  criticalPath?: number[]
  onTaskClick?: (task: Task) => void
  height?: number
  showLegend?: boolean
}

export const GanttChart: React.FC<GanttChartProps> = ({
  tasks,
  criticalPath = [],
  onTaskClick,
  height = 600,
  showLegend = true
}) => {
  const [hoveredTaskId, setHoveredTaskId] = useState<number | null>(null)
  
  // Transform tasks untuk Gantt visualization
  const chartData = useMemo(() => {
    if (!tasks || tasks.length === 0) return []
    
    // Normalize dates
    const allDates = tasks.flatMap((t) => [new Date(t.startDate), new Date(t.endDate)])
    const minDate = new Date(Math.min(...allDates.map((d) => d.getTime())))
    const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())))
    
    // Convert to chart format
    return tasks.map((task) => {
      const taskStart = new Date(task.startDate)
      const taskEnd = new Date(task.endDate)
      
      // Days from project start
      const startDays = Math.floor(
        (taskStart.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
      )
      const durationDays = task.duration || 1
      const progress = task.progress || 0
      
      return {
        id: task.id,
        name: task.taskName.length > 30 
          ? task.taskName.substring(0, 27) + '...' 
          : task.taskName,
        startDays,
        durationDays,
        progress,
        status: task.status,
        isCritical: criticalPath.includes(task.id),
        task // Store full task for click handling
      }
    })
  }, [tasks, criticalPath])
  
  // Status color mapping
  const getStatusColor = (status: string, isCritical: boolean, isHovered: boolean) => {
    const hoverOpacity = isHovered ? 'A0' : 'FF'
    
    if (isCritical) {
      // Critical path tasks are red
      return `#EF4444${hoverOpacity}` // Red
    }
    
    switch (status) {
      case 'completed':
        return `#10B981${hoverOpacity}` // Green
      case 'in_progress':
        return `#3B82F6${hoverOpacity}` // Blue
      case 'delayed':
        return `#F59E0B${hoverOpacity}` // Orange
      case 'planned':
      default:
        return `#9CA3AF${hoverOpacity}` // Gray
    }
  }
  
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg text-xs">
          <p className="font-semibold text-gray-900">{data.task.taskName}</p>
          <p className="text-gray-700">Status: <span className="capitalize">{data.status}</span></p>
          <p className="text-gray-700">Duration: {data.durationDays} days</p>
          <p className="text-blue-600">Progress: {data.progress.toFixed(1)}%</p>
          <p className="text-gray-600 text-xs mt-1">
            {new Date(data.task.startDate).toLocaleDateString()} - {new Date(data.task.endDate).toLocaleDateString()}
          </p>
        </div>
      )
    }
    return null
  }
  
  if (chartData.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-300">
        <p className="text-gray-500">No tasks available. Generate tasks from BoQ first.</p>
      </div>
    )
  }
  
  // Show all tasks in Gantt chart
  const displayTasks = chartData
  const totalDays = Math.max(...displayTasks.map((t) => t.startDays + t.durationDays))
  
  return (
    <div className="w-full space-y-4">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={displayTasks}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 250, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            label={{ value: 'Days', position: 'insideBottomRight', offset: -5 }}
            domain={[0, totalDays]}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={250}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend />}
          
          {/* Task duration bar */}
          <Bar
            dataKey="durationDays"
            stackId="task"
            fill="#ccc"
            isAnimationActive={false}
            onClick={(data) => onTaskClick?.(data.task)}
          >
            {displayTasks.map((entry) => (
              <Cell
                key={entry.id}
                fill={getStatusColor(
                  entry.status,
                  entry.isCritical,
                  hoveredTaskId === entry.id
                )}
              />
            ))}
          </Bar>
          
          {/* Progress indicator (stacked on top) */}
          <Bar
            dataKey="progress"
            stackId="progress"
            fill="#4CAF50"
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      
      {/* Legend */}
      <div className="bg-gray-50 p-3 rounded border text-xs space-y-1">
        <div className="font-semibold text-gray-900 mb-2">Legend:</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500"></div>
            <span>Critical Path</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500"></div>
            <span>In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500"></div>
            <span>Delayed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500"></div>
            <span>Planned</span>
          </div>
        </div>
      </div>
      
      {/* Info */}
      <div className="text-xs text-gray-600 p-2 bg-blue-50 rounded border border-blue-200">
        <p>📊 Showing {displayTasks.length} tasks (out of {chartData.length} total)</p>
        <p>💡 Bar length = task duration | Green fill = completed percentage</p>
        {criticalPath.length > 0 && (
          <p className="mt-1">⚠️ {criticalPath.length} tasks on critical path (delay will impact project completion)</p>
        )}
      </div>
    </div>
  )
}